import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import SiteSearch from '../Common/SiteSearch';
import SearchModal from '../Common/SearchModal';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Menu as MenuIcon, X as XIcon, ChevronDown, ChevronRight,
    Bell, Globe, GraduationCap, BookOpen, Building2,
    Users, Shield, FileText, Youtube,
    FileCheck, UserCog, FolderOpen,
    Camera, Microscope, ExternalLink,
    ArrowRight, Layers, Search, Share2,
    MapPin, Heart, Home
} from 'lucide-react';
import { FaInstagram, FaTwitter, FaYoutube, FaLinkedinIn } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { trackNavigation, trackExternalClick, trackButtonClick } from '../../utils/analytics';
import { useDuyurular } from '../../hooks/useSupabaseData';
import { fetchAkademikFaaliyet } from '../../utils/akademikFaaliyetCache';
import './Navbar.css';
import { dateLocale, localizePath, toEnPath, toTrPath } from '../../utils/locale';

const getNavLinks = (t, currentLang) => {
    return [
        { title: currentLang === 'en' ? 'Home' : 'Ana Sayfa', href: localizePath('/') },

        { title: currentLang === 'en' ? 'About Us' : 'Fakültemiz', sublinks: [
            { isGroup: true, title: currentLang === 'en' ? 'Institutional'       : 'Kurumsal',            groupIcon: 'Building2',  groupColor: '#2563EB' },
            { title: t('nav.history'),                  href: localizePath('/kurumsal/tarihce') },
            { title: t('nav.missionVision'),            href: localizePath('/kurumsal/misyon-vizyon') },
            { title: t('nav.fedek'),                    href: localizePath('/kurumsal/fedek') },
            { title: t('nav.strategicPlan'),            href: localizePath('/kurumsal/stratejik-plan') },
            { title: t('nav.formerDeans'),              href: localizePath('/kurumsal/eski-dekanlar') },
            { isGroup: true, title: currentLang === 'en' ? 'Management'          : 'Yönetim',             groupIcon: 'Users',      groupColor: '#7C3AED' },
            { title: t('nav.dean'),                     href: localizePath('/yonetim/dekan') },
            { title: t('nav.viceDeans'),                href: localizePath('/yonetim/dekan-yardimcilari') },
            { title: t('nav.facultySecretary'),         href: localizePath('/yonetim/fakulte-sekreteri') },
            { title: t('nav.facultyBoard'),             href: localizePath('/yonetim/fakulte-kurulu') },
            { title: t('nav.managementBoard'),          href: localizePath('/yonetim/yonetim-kurulu') },
            { title: t('nav.unitActivities'),           href: localizePath('/birim-faaliyetleri') },
            { isGroup: true, title: currentLang === 'en' ? 'Boards & Commissions': 'Kurul & Komisyonlar', groupIcon: 'Shield',     groupColor: '#EA580C' },
            { title: t('nav.qualityStrategy'),          href: localizePath('/kurul-komisyonlar/kalite-strateji') },
            { title: t('nav.academicQuality'),          href: localizePath('/kurul-komisyonlar/akademik-birim-kalite') },
            { title: t('nav.scholarship'),              href: localizePath('/kurul-komisyonlar/burs-komisyonu') },
            { title: t('nav.promotionCeremony'),        href: localizePath('/kurul-komisyonlar/tanitim-toren') },
            { title: t('nav.publication'),              href: localizePath('/kurul-komisyonlar/yayin') },
            { title: t('nav.nonFormalLearning'),        href: localizePath('/kurul-komisyonlar/program-disi-ogrenme') },
            { title: t('nav.advisoryBoard'),            href: localizePath('/kurul-komisyonlar/birim-danisma-kurulu') },
            { isGroup: true, title: currentLang === 'en' ? 'Decisions'           : 'Kararlar',            groupIcon: 'FileCheck',  groupColor: '#0891B2' },
            { title: t('nav.facultyBoardDecisions'),    href: localizePath('/kararlar/fakulte-kurulu') },
            { title: t('nav.managementBoardDecisions'), href: localizePath('/kararlar/fakulte-yonetim') },
        ]},

        { title: currentLang === 'en' ? 'For Applicants' : 'Aday Öğrenciler', sublinks: [
            { isGroup: true, title: currentLang === 'en' ? 'Our Departments' : 'Bölümlerimiz', groupIcon: 'Layers',       groupColor: '#059669' },
            { title: t('nav.actuarialScience'),     href: 'https://aktuerya.hacettepe.edu.tr', external: true },
            { title: t('nav.biology'),              href: 'https://biology.hacettepe.edu.tr',  external: true },
            { title: t('nav.chemistry'),            href: 'https://chem.hacettepe.edu.tr',     external: true },
            { title: t('nav.mathematics'),          href: 'https://mat.hacettepe.edu.tr',      external: true },
            { title: t('nav.statistics'),           href: 'https://stat.hacettepe.edu.tr',     external: true },
            { isGroup: true, title: currentLang === 'en' ? 'Presentations'  : 'Tanıtımlar',   groupIcon: 'Youtube',       groupColor: '#DC2626' },
            { title: currentLang === 'en' ? 'Hacettepe University Official Presentation 2026' : 'Hacettepe Üniversitesi Resmi Tanıtımı 2026', href: 'https://www.youtube.com/watch?v=cjAKv8mOdQI', external: true, isNew: true },
            { title: t('navbar.facultyPromotion'),     href: 'https://www.youtube.com/watch?v=f7tTbfwei9Q', external: true },
            { title: t('navbar.biologyPromotion'),     href: 'https://www.youtube.com/watch?v=6OqEm9H9knk', external: true },
            { title: t('navbar.chemistryPromotion'),   href: 'https://www.youtube.com/watch?v=CgduFVGf6LI', external: true },
            { title: t('navbar.actuarialPromotion'),   href: 'https://aktuerya.hacettepe.edu.tr/duyurular/tanitim25.pdf', external: true },
            { title: t('navbar.mathematicsPromotion'), href: 'https://mat.hacettepe.edu.tr/duyurular/tanitim_sunum.pdf', external: true },
            { title: t('navbar.statisticsPromotion'),  href: 'https://www.youtube.com/watch?v=rMbJeSIByPA', external: true },
            { title: t('navbar.chemistryVirtualTour'), href: localizePath('/bolumler/kimya-bolumu-sanal-tur') },
            { isGroup: true, title: currentLang === 'en' ? 'Programs'       : 'Programlar',   groupIcon: 'GraduationCap', groupColor: '#3B82F6' },
            { title: t('nav.undergraduate'),        href: localizePath('/programlar/lisans') },
            { title: t('nav.masters'),              href: localizePath('/programlar/yuksek-lisans') },
            { title: t('nav.doctorate'),            href: localizePath('/programlar/doktora') },
            { title: t('nav.minor'),                href: localizePath('/programlar/yandal') },
            { title: t('nav.doubleMajor'),          href: localizePath('/programlar/cift-anadal') },
            { title: t('nav.erasmus'),              href: localizePath('/programlar/erasmus') },
        ]},

        { title: currentLang === 'en' ? 'Students' : 'Öğrenciler', sublinks: [
            { isGroup: true, title: currentLang === 'en' ? 'Guide'          : 'Rehber',       groupIcon: 'BookOpen',      groupColor: '#D97706' },
            { title: t('nav.studentGuide'),         href: 'https://fs.hacettepe.edu.tr/fenyeni/dosyalar/ogrencikilavuzu/ogrencikilavuzu.pdf', external: true },
            { title: t('nav.usefulDocuments'),      href: localizePath('/belgeler/ogrenci') },
            { isGroup: true, title: currentLang === 'en' ? 'Activities'     : 'Faaliyetler',  groupIcon: 'Heart',         groupColor: '#EC4899' },
            { title: t('nav.internship'),           href: localizePath('/staj') },
            { title: t('nav.studentClubs'),         href: localizePath('/ogrenci-topluluklari') },
            { title: t('nav.erasmus'),              href: localizePath('/programlar/erasmus') },
            { title: t('nav.internationalStudents'),href: currentLang === 'en' ? '/en/student/international-students' : '/ogrenci/uluslararasi-ogrenciler' },
            { title: t('nav.accessibleLife'),       href: localizePath('/engelsiz-yasam') },
            { title: t('nav.socialResponsibility'), href: localizePath('/sosyal-sorumluluk-projeleri') },
        ]},

        { title: currentLang === 'en' ? 'Research' : 'Araştırma', sublinks: [
            { isGroup: true, title: currentLang === 'en' ? 'Infrastructure'  : 'Altyapı',     groupIcon: 'Microscope',    groupColor: '#059669' },
            { title: t('nav.labs'),                 href: localizePath('/arastirma/laboratuvarlar') },
            { title: t('nav.ongoingProjects'),      href: localizePath('/arastirma/devam-eden-projelerimiz') },
            { title: t('nav.museums'),              href: localizePath('/arastirma/muzeler') },
            { isGroup: true, title: currentLang === 'en' ? 'Publications'    : 'Yayınlar',    groupIcon: 'FileText',      groupColor: '#6366F1' },
            { title: t('nav.journals'),             href: localizePath('/arastirma/dergiler') },
            { title: t('nav.books'),                href: localizePath('/arastirma/kitaplar-editorlukler') },
            { title: t('nav.academicActivity'),     href: localizePath('/akademik-faaliyet') },
        ]},

        { title: currentLang === 'en' ? 'Staff' : 'Personel', sublinks: [
            { isGroup: true, title: currentLang === 'en' ? 'Staff'          : 'Kadro',        groupIcon: 'UserCog',    groupColor: '#0891B2' },
            { title: t('nav.faculty'),              href: localizePath('/fakulte-personeli/ogretim-uyeleri') },
            { title: t('nav.administrativeStaff'),  href: localizePath('/fakulte-personeli/idari-personel') },
            { title: t('nav.retiredFaculty'),       href: localizePath('/fakulte-personeli/emekli-ogretim-elemanlari') },
            { isGroup: true, title: currentLang === 'en' ? 'Documents & Other': 'Belgeler & Diğer', groupIcon: 'FolderOpen', groupColor: '#64748B' },
            { title: t('nav.academicAdministrative'),href: localizePath('/belgeler/akademik-idari-personel') },
            { title: t('nav.lawsRegulations'),      href: localizePath('/belgeler/kanun-yonetmelikler') },
            { title: t('nav.unitActivities'),       href: localizePath('/birim-faaliyetleri') },
        ]},

        { title: t('nav.contact'),                  href: localizePath('/iletisim') },
    ];
};

const getDepartmentPromotions = (t, currentLang) => {
    return [
        { title: t('navbar.facultyPromotion'), href: "https://www.youtube.com/watch?v=f7tTbfwei9Q", icon: "Youtube", external: true },
        { title: t('navbar.biologyPromotion'), href: "https://www.youtube.com/watch?v=6OqEm9H9knk", icon: "Youtube", external: true },
        { title: t('navbar.chemistryPromotion'), href: "https://www.youtube.com/watch?v=CgduFVGf6LI", icon: "Youtube", external: true },
        { title: t('navbar.actuarialPromotion'), href: "https://aktuerya.hacettepe.edu.tr/duyurular/tanitim25.pdf", icon: "FileText", external: true },
        { title: t('navbar.mathematicsPromotion'), href: "https://mat.hacettepe.edu.tr/duyurular/tanitim_sunum.pdf", icon: "FileText", external: true },
        { title: t('navbar.statisticsPromotion'), href: "https://www.youtube.com/watch?v=rMbJeSIByPA", icon: "Youtube", external: true },
        { title: "divider", isDivider: true },
        { title: t('navbar.virtualTours'), isHeader: true },
        { title: t('navbar.chemistryVirtualTour'), href: localizePath('/bolumler/kimya-bolumu-sanal-tur'), icon: "Building2", external: false },
    ];
};

const HEADER_SOCIAL = [
    { href: 'https://www.instagram.com/hacettepefenfakultesi', Icon: FaInstagram, title: 'Instagram',    color: '#E1306C' },
    { href: 'https://x.com/hacettepefenfak',                  Icon: FaTwitter,   title: 'X (Twitter)',  color: '#111827' },
    { href: 'https://www.youtube.com/@hacettepeunifenfakultesi', Icon: FaYoutube, title: 'YouTube', color: '#FF0000' },
    { href: 'https://tr.linkedin.com/in/fen-fak%C3%BCltesi-hacettepe-%C3%BCniversitesi-32775a234', Icon: FaLinkedinIn, title: 'LinkedIn', color: '#0077B5' },
];

const DEFAULT_PHOTO = 'https://fs.hacettepe.edu.tr/fenyeni/basliksiz_klasor/FF.jpg';

const ICON_MAP = {
    Building2, Users, Shield, FileCheck, Layers, GraduationCap,
    BookOpen, Heart, Microscope, FileText, UserCog, FolderOpen,
    Camera, MapPin, Home, Search, Youtube,
};

// index sırası getNavLinks dönüş sırasıyla eşleşmeli
const NAV_META = [
    { Icon: Home,         photo: null },
    { Icon: Building2,    photo: 'https://images.unsplash.com/photo-1562774053-701939374585?auto=format&fit=crop&w=1400&q=80' },
    { Icon: GraduationCap,photo: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?auto=format&fit=crop&w=1400&q=80' },
    { Icon: BookOpen,     photo: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?auto=format&fit=crop&w=1400&q=80' },
    { Icon: Microscope,   photo: 'https://images.unsplash.com/photo-1532187863486-abf9dbad1b69?auto=format&fit=crop&w=1400&q=80' },
    { Icon: Users,        photo: 'https://images.unsplash.com/photo-1524178232363-1fb2b075b655?auto=format&fit=crop&w=1400&q=80' },
    { Icon: MapPin,       photo: null },
];

/* ─── YENİ TAM EKRAN MENÜ ─── */
const MegaMenu = ({ isOpen, setIsOpen, changeLanguage, currentLang }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [openIdx, setOpenIdx]       = useState(null);
    const [showSearch, setShowSearch] = useState(false);
    const navLinks = useMemo(() => getNavLinks(t, i18n.language), [t, i18n.language]);
    const { duyurular } = useDuyurular();
    const latestTwo = useMemo(() =>
        [...duyurular]
            .sort((a, b) => new Date(b.date || b.tarih || 0) - new Date(a.date || a.tarih || 0))
            .slice(0, 2),
    [duyurular]);

    const close = useCallback(() => setIsOpen(false), [setIsOpen]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setOpenIdx(null);
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    useEffect(() => {
        const onKey = e => { if (e.key === 'Escape') close(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [close]);

    const SOCIAL = [
        { label: 'Instagram', href: 'https://www.instagram.com/hacettepeuni/', Icon: FaInstagram, color: '#E1306C', hoverBg: '#fce7f3' },
        { label: 'Twitter / X', href: 'https://twitter.com/hacettepeuni', Icon: FaTwitter, color: '#111827', hoverBg: '#f3f4f6' },
        { label: 'YouTube', href: 'https://www.youtube.com/@HacettepeUniversitesi', Icon: FaYoutube, color: '#FF0000', hoverBg: '#fee2e2' },
        { label: 'LinkedIn', href: 'https://www.linkedin.com/school/hacettepe-university/', Icon: FaLinkedinIn, color: '#0077B5', hoverBg: '#dbeafe' },
    ];

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    key="menu-overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.22 }}
                    className="fixed inset-0 z-[1100] flex"
                >
                    {/* ── Sol: Sabit Fotoğraf ── */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="relative flex-1 hidden md:block cursor-pointer overflow-hidden"
                        onClick={close}
                    >
                        <img
                            src={DEFAULT_PHOTO}
                            alt="Hacettepe Fen Fakültesi"
                            className="absolute inset-0 w-full h-full object-cover"
                            style={{ filter: 'brightness(0.60) saturate(0.80)' }}
                        />
                        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg,rgba(5,2,8,0.4) 0%,rgba(0,0,0,0.2) 100%)' }} />
                    </motion.div>

                    {/* ── Sağ: Beyaz panel ── */}
                    <motion.div
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'tween', duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                        className="mega-menu-panel relative bg-white flex flex-col overflow-hidden"
                        style={{ width: 'min(420px, 100vw)', boxShadow: '-4px 0 32px rgba(0,0,0,0.12)' }}
                        onClick={e => e.stopPropagation()}
                    >
                        {/* Logo */}
                        <div className="flex-shrink-0 flex items-center gap-3 px-8 pt-7 pb-5 border-b border-gray-100">
                            <div className="flex-1 min-w-0">
                                <img
                                    src="/fenfaklogo.png"
                                    alt="Hacettepe Üniversitesi Fen Fakültesi"
                                    className="h-10 w-auto max-w-full object-contain"
                                />
                            </div>
                            <img
                                src="https://iysportal.hacettepe.edu.tr/templates/template3/images/yokak5.svg"
                                alt="YÖKAK"
                                className="h-9 opacity-80"
                            />
                        </div>

                        {/* Kapat — sağ üst köşe */}
                        <button
                            onClick={close}
                            className="absolute top-5 right-6 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-800 hover:text-white text-gray-400 transition-all duration-200"
                            title={currentLang === 'en' ? 'Close' : 'Kapat'}
                        >
                            <XIcon size={14} />
                        </button>

                        {/* Üst bar: ikonlar */}
                        <div className="flex-shrink-0 flex items-center px-8 pt-5 pb-5">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                                {/* Sosyal Medya — yan yana ikonlar */}
                                <div className="flex items-center gap-1 flex-shrink-0">
                                    {SOCIAL.map(({ label, href, Icon, color, hoverBg }) => (
                                        <a key={label} href={href} target="_blank" rel="noopener noreferrer"
                                           title={label}
                                           className="w-8 h-8 flex items-center justify-center rounded-full transition-all duration-200 hover:scale-105"
                                           style={{ color: '#9CA3AF' }}
                                           onMouseEnter={e => { e.currentTarget.style.color = color; e.currentTarget.style.background = hoverBg; }}
                                           onMouseLeave={e => { e.currentTarget.style.color = '#9CA3AF'; e.currentTarget.style.background = 'transparent'; }}>
                                            <Icon size={16} />
                                        </a>
                                    ))}
                                </div>

                                {/* Arama — yana açılan input */}
                                <div className="flex-1 flex justify-end min-w-0">
                                    <AnimatePresence mode="wait">
                                        {showSearch ? (
                                            <motion.div
                                                key="search-input"
                                                initial={{ width: 36, opacity: 0.6 }}
                                                animate={{ width: '100%', opacity: 1 }}
                                                exit={{ width: 36, opacity: 0 }}
                                                transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
                                                className="flex items-center gap-2 bg-gray-100 rounded-full px-3 overflow-hidden"
                                                style={{ height: 36 }}
                                            >
                                                <Search size={13} className="text-gray-400 flex-shrink-0" />
                                                <input
                                                    autoFocus
                                                    type="text"
                                                    placeholder={currentLang === 'en' ? 'Search…' : 'Ara…'}
                                                    className="flex-1 bg-transparent text-[13px] text-gray-700 outline-none placeholder-gray-400 min-w-0"
                                                    onKeyDown={e => {
                                                        if (e.key === 'Enter' && e.target.value.trim()) {
                                                            close();
                                                            navigate(`${currentLang === 'en' ? '/en' : ''}/arama?q=${encodeURIComponent(e.target.value.trim())}`);
                                                        }
                                                        if (e.key === 'Escape') setShowSearch(false);
                                                    }}
                                                />
                                                <button
                                                    onClick={() => setShowSearch(false)}
                                                    className="flex-shrink-0 text-gray-400 hover:text-gray-700 transition-colors"
                                                >
                                                    <XIcon size={13} />
                                                </button>
                                            </motion.div>
                                        ) : (
                                            <motion.button
                                                key="search-btn"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                transition={{ duration: 0.15 }}
                                                onClick={() => { setShowSearch(true); }}
                                                className="w-9 h-9 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-800 hover:text-white text-gray-500 transition-all duration-200 flex-shrink-0"
                                                title={currentLang === 'en' ? 'Search' : 'Ara'}
                                            >
                                                <Search size={15} />
                                            </motion.button>
                                        )}
                                    </AnimatePresence>
                                </div>
                            </div>
                        </div>

                        {/* Menü linkleri */}
                        <div className="flex-1 overflow-y-auto px-8 pb-10">
                            {navLinks.map((link, idx) => {
                                const meta = NAV_META[idx];
                                const Icon = meta?.Icon;
                                const isActive = openIdx === idx;
                                return (
                                <div key={idx} className="border-b border-gray-100 last:border-0">
                                    {link.sublinks ? (
                                        <>
                                            <button
                                                onClick={() => setOpenIdx(isActive ? null : idx)}
                                                className="w-full flex items-center gap-3 py-3.5 text-left group"
                                            >
                                                {Icon && (
                                                    <div className={`w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-200 ${isActive ? 'bg-[#7B0D1E]' : 'bg-gray-100 group-hover:bg-[#7B0D1E]/10'}`}>
                                                        <Icon size={15} className={isActive ? 'text-white' : 'text-gray-400 group-hover:text-[#7B0D1E]'} />
                                                    </div>
                                                )}
                                                <span className={`flex-1 font-display font-bold tracking-tight transition-colors duration-150 ${isActive ? 'text-[#7B0D1E]' : 'text-gray-900 group-hover:text-[#7B0D1E]'}`}
                                                      style={{ fontSize: '1.02rem' }}>
                                                    {link.title}
                                                </span>
                                                <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-200 ${isActive ? 'bg-[#7B0D1E]/12' : 'bg-gray-100'}`}>
                                                    <ChevronDown
                                                        size={13}
                                                        className={`transition-transform duration-200 ${isActive ? 'text-[#7B0D1E]' : 'text-gray-500'}`}
                                                        style={{ transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)' }}
                                                    />
                                                </div>
                                            </button>
                                            <AnimatePresence>
                                                {openIdx === idx && (
                                                    <motion.div
                                                        initial={{ height: 0, opacity: 0 }}
                                                        animate={{ height: 'auto', opacity: 1 }}
                                                        exit={{ height: 0, opacity: 0 }}
                                                        transition={{ duration: 0.18, ease: 'easeOut' }}
                                                        style={{ overflow: 'hidden' }}
                                                    >
                                                        <div className="pb-3 pl-3">
                                                            {link.sublinks.map((sub, sIdx) => {
                                                                if (sub.isGroup) {
                                                                    const GIcon = sub.groupIcon ? ICON_MAP[sub.groupIcon] : null;
                                                                    const gColor = sub.groupColor || '#6B7280';
                                                                    return (
                                                                        <div key={sIdx} className="flex items-center gap-2 mt-4 mb-1.5 first:mt-1">
                                                                            {GIcon && (
                                                                                <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0"
                                                                                     style={{ background: gColor + '1a' }}>
                                                                                    <GIcon size={11} style={{ color: gColor }} />
                                                                                </div>
                                                                            )}
                                                                            <span className="font-display text-[10px] font-bold uppercase tracking-[0.2em]"
                                                                                  style={{ color: gColor }}>
                                                                                {sub.title}
                                                                            </span>
                                                                            <div className="flex-1 h-px" style={{ background: gColor + '25' }} />
                                                                        </div>
                                                                    );
                                                                }
                                                                const cls = "flex items-center gap-2.5 py-1.5 font-display text-[0.84rem] font-medium text-gray-600 hover:text-[#7B0D1E] transition-colors group";
                                                                const dot = <div className="w-1 h-1 rounded-full bg-gray-300 group-hover:bg-[#7B0D1E] flex-shrink-0 transition-colors" />;
                                                                const label = (
                                                                    <>
                                                                        <span className={sub.isNew ? 'underline decoration-red-500 underline-offset-2' : ''}>{sub.title}</span>
                                                                        {sub.isNew && (
                                                                            <span className="text-[8px] font-black tracking-wider text-white bg-red-600 rounded px-1.5 py-0.5 flex-shrink-0">
                                                                                {currentLang === 'en' ? 'NEW' : 'YENİ'}
                                                                            </span>
                                                                        )}
                                                                    </>
                                                                );
                                                                return sub.external ? (
                                                                    <a key={sIdx} href={sub.href} target="_blank" rel="noopener noreferrer" onClick={close} className={cls}>
                                                                        {dot}{label}
                                                                    </a>
                                                                ) : (
                                                                    <Link key={sIdx} to={sub.href} onClick={close} className={cls}>
                                                                        {dot}{label}
                                                                    </Link>
                                                                );
                                                            })}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </>
                                    ) : (
                                        <Link
                                            to={link.href}
                                            onClick={close}
                                            className="flex items-center gap-3 py-3.5 font-display font-bold tracking-tight text-gray-900 hover:text-[#7B0D1E] transition-colors duration-150 group"
                                            style={{ fontSize: '1.02rem' }}
                                        >
                                            {Icon && (
                                                <div className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0 bg-gray-100 group-hover:bg-[#7B0D1E]/10 transition-all duration-200">
                                                    <Icon size={15} className="text-gray-400 group-hover:text-[#7B0D1E]" />
                                                </div>
                                            )}
                                            {link.title}
                                        </Link>
                                    )}
                                </div>
                                );
                            })}
                        </div>

                        {/* ── Son Duyurular ── */}
                        {latestTwo.length > 0 && (
                            <div className="flex-shrink-0 border-t border-gray-100 px-8 py-5">
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="font-display text-xs font-bold uppercase tracking-[0.22em]" style={{ color: '#7B0D1E' }}>{currentLang === 'en' ? 'Latest Announcements' : 'Son Duyurular'}</span>
                                    <div className="flex-1 h-px bg-gray-100" />
                                    <Link to={localizePath('/duyurular')} onClick={close}
                                          className="text-[9px] font-bold text-[#7B0D1E] hover:underline tracking-wide">
                                        {currentLang === 'en' ? 'All →' : 'Tümü →'}
                                    </Link>
                                </div>
                                <div className="flex flex-col gap-3">
                                    {latestTwo.map((d, i) => {
                                        const raw = d.date || d.tarih || '';
                                        const dateObj = raw ? new Date(raw) : null;
                                        const day   = dateObj ? dateObj.toLocaleDateString(dateLocale(), { day: '2-digit' }) : '—';
                                        const month = dateObj ? dateObj.toLocaleDateString(dateLocale(), { month: 'short' }).replace('.', '') : '';
                                        const year  = dateObj ? dateObj.getFullYear() : '';
                                        const href  = d.link?.startsWith('http') ? d.link : localizePath(d.slug ? `/duyurular/${d.slug}` : d.id ? `/duyurular/${d.id}` : '/duyurular');
                                        const isExt = d.link?.startsWith('http');
                                        const inner = (
                                            <div className="flex items-stretch gap-4 group cursor-pointer">
                                                {/* Tarih bloğu */}
                                                <div className="flex-shrink-0 flex flex-col items-center justify-center w-14 h-14 rounded-2xl bg-[#7B0D1E]/6 border border-[#7B0D1E]/12 group-hover:bg-[#7B0D1E] transition-all duration-200">
                                                    <span className="font-display text-[1.2rem] font-extrabold tracking-tight leading-none text-[#7B0D1E] group-hover:text-white transition-colors">
                                                        {day}
                                                    </span>
                                                    <span className="text-[9.5px] font-black uppercase tracking-wide text-[#7B0D1E] group-hover:text-white transition-colors leading-none mt-0.5">
                                                        {month}
                                                    </span>
                                                    <span className="text-[9px] font-bold text-[#7B0D1E]/70 group-hover:text-white/80 transition-colors leading-none mt-0.5">
                                                        {year}
                                                    </span>
                                                </div>
                                                {/* Başlık */}
                                                <div className="flex-1 flex flex-col justify-center min-w-0">
                                                    <p className="text-[12.5px] font-medium text-gray-700 group-hover:text-[#7B0D1E] transition-colors leading-snug line-clamp-2">
                                                        {d.title || d.baslik}
                                                    </p>
                                                </div>
                                                <div className="flex-shrink-0 flex items-center">
                                                    <ArrowRight size={13} className="text-gray-300 group-hover:text-[#7B0D1E] group-hover:translate-x-0.5 transition-all" />
                                                </div>
                                            </div>
                                        );
                                        return isExt ? (
                                            <a key={i} href={href} target="_blank" rel="noopener noreferrer" onClick={close}>{inner}</a>
                                        ) : (
                                            <Link key={i} to={href} onClick={close}>{inner}</Link>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

/* ─── ESKİ MEGA MENÜ (yedek) ─── */
const MegaMenuLegacy = ({ isOpen, setIsOpen, changeLanguage, currentLang }) => {
    const { t, i18n } = useTranslation();
    const { duyurular } = useDuyurular();
    const [activeIdx, setActiveIdx] = useState(null);
    const [yayinSayisi, setYayinSayisi] = useState(null);
    const [yayinHata, setYayinHata] = useState(false);

    const close = useCallback(() => setIsOpen(false), [setIsOpen]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            setActiveIdx(null);
            fetchAkademikFaaliyet()
                .then(data => setYayinSayisi(data.makaleler.length))
                .catch(() => setYayinHata(true));
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') close(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [close]);

    const navLinks = useMemo(() => getNavLinks(t, i18n.language), [t, i18n.language]);

    const sections = useMemo(() => {
        const find = (key) => navLinks.find(l => l.title === t(key));
        return [
            { label: t('nav.institutional'),    icon: Building2,     color: '#E11D48', links: find('nav.institutional')?.sublinks    || [] },
            { label: t('nav.management'),        icon: Users,         color: '#2563EB', links: find('nav.management')?.sublinks        || [] },
            { label: t('nav.boardsCommissions'), icon: Shield,        color: '#7C3AED', links: find('nav.boardsCommissions')?.sublinks || [] },
            { label: t('nav.facultyDecisions'),  icon: FileCheck,     color: '#EA580C', links: find('nav.facultyDecisions')?.sublinks  || [] },
            { label: t('nav.facultyPersonnel'),  icon: UserCog,       color: '#0891B2', links: find('nav.facultyPersonnel')?.sublinks  || [] },
            { label: t('nav.research'),          icon: Microscope,    color: '#059669', links: find('nav.research')?.sublinks          || [] },
            { label: t('nav.student'),           icon: GraduationCap, color: '#D97706', links: find('nav.student')?.sublinks           || [] },
            { label: t('nav.programs'),          icon: BookOpen,      color: '#3B82F6', links: find('nav.programs')?.sublinks          || [] },
            { label: t('nav.documents'),         icon: FolderOpen,    color: '#64748B', links: find('nav.documents')?.sublinks         || [] },
            { label: t('nav.gallery'),           icon: Camera,        color: '#DB2777', links: find('nav.gallery')?.sublinks           || [] },
        ];
    }, [t, navLinks]);

    const singleLinks = useMemo(() => {
        const find = (key) => navLinks.find(l => l.title === t(key));
        return [find('nav.accessibleLife'), find('nav.socialResponsibility'), find('nav.contact')]
            .filter(l => l && !l.sublinks);
    }, [t, navLinks]);

    const latestAnnouncement = useMemo(() =>
        duyurular.length > 0
            ? [...duyurular].sort((a, b) => new Date(b.date) - new Date(a.date))[0]
            : null,
    [duyurular]);

    const deptCards = useMemo(() => [
        { title: 'Aktüerya Bilimleri', href: 'https://aktuerya.hacettepe.edu.tr', color: '#9F1239', short: 'AKT' },
        { title: 'Biyoloji',           href: 'https://biology.hacettepe.edu.tr',  color: '#166534', short: 'BİY' },
        { title: 'Kimya',              href: 'https://chem.hacettepe.edu.tr',     color: '#1E3A8A', short: 'KİM' },
        { title: 'Matematik',          href: 'https://mat.hacettepe.edu.tr',      color: '#4C1D95', short: 'MAT' },
        { title: 'İstatistik',         href: 'https://stat.hacettepe.edu.tr',     color: '#92400E', short: 'İST' },
    ], []);

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        key="backdrop"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.15 }}
                        className="fixed inset-0 z-[1090]"
                        style={{ background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(3px)' }}
                        onClick={close}
                    />

                    {/* ── Sağdan kayan panel ── */}
                    <motion.div
                        key="panel"
                        initial={{ x: '100%' }}
                        animate={{ x: 0 }}
                        exit={{ x: '100%' }}
                        transition={{ type: 'tween', duration: 0.22, ease: [0.25, 0.46, 0.45, 0.94] }}
                        className="fixed top-0 right-0 bottom-0 z-[1100] flex flex-col bg-white overflow-hidden"
                        style={{ width: 'min(480px, 100vw)', boxShadow: '-6px 0 40px rgba(0,0,0,0.16)' }}
                    >
                        {/* ── Header ── */}
                        <div className="flex-shrink-0 px-5 py-4 border-b border-gray-100 flex items-center justify-between gap-4">
                            <div className="flex items-center min-w-0">
                                <img src="/fenfaklogo.png"
                                     alt="Hacettepe Üniversitesi Fen Fakültesi" className="h-9 w-auto max-w-full object-contain" />
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Dil */}
                                <div className="flex bg-gray-100 rounded-lg p-0.5 gap-0.5">
                                    {['tr', 'en'].map(lang => (
                                        <button key={lang} onClick={() => changeLanguage(lang)}
                                                className={`px-2.5 py-1 rounded-md text-[10px] font-black uppercase tracking-widest transition-all ${
                                                    currentLang === lang ? 'bg-[#7B0D1E] text-white' : 'text-gray-400 hover:text-gray-700'
                                                }`}>{lang}</button>
                                    ))}
                                </div>
                                <button onClick={close}
                                        className="w-9 h-9 flex items-center justify-center rounded-xl bg-gray-100 hover:bg-[#7B0D1E] text-gray-500 hover:text-white transition-all duration-200">
                                    <XIcon size={16} />
                                </button>
                            </div>
                        </div>

                        {/* ── Arama ── */}
                        <div className="flex-shrink-0 px-5 py-3 border-b border-gray-50">
                            <SiteSearch />
                        </div>

                        {/* ── Kaydırılabilir gövde ── */}
                        <div className="flex-1 overflow-y-auto">

                            {/* ── İkon grid — her satırın hemen altında alt menü açılır ── */}
                            <div className="px-5 pt-5 pb-2">
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="text-[11px] font-black uppercase tracking-widest text-gray-500">Menü</span>
                                    <div className="flex-1 h-px bg-gray-200" />
                                </div>

                                {Array.from({ length: Math.ceil(sections.length / 2) }, (_, rowIdx) => {
                                    const rowSections = sections.slice(rowIdx * 2, rowIdx * 2 + 2);
                                    const activeColInRow = rowSections.findIndex((_, colIdx) => rowIdx * 2 + colIdx === activeIdx);
                                    const activeSecInRow = activeColInRow >= 0 ? sections[rowIdx * 2 + activeColInRow] : null;

                                    return (
                                        <div key={rowIdx} className="mb-2">
                                            {/* Satırdaki kartlar */}
                                            <div className="grid grid-cols-2 gap-2">
                                                {rowSections.map((sec, colIdx) => {
                                                    const idx = rowIdx * 2 + colIdx;
                                                    const active = idx === activeIdx;
                                                    const Icon = sec.icon;
                                                    return (
                                                        <button
                                                            key={idx}
                                                            onClick={() => setActiveIdx(active ? null : idx)}
                                                            className="flex items-center gap-3 p-3.5 rounded-2xl border-2 text-left transition-all duration-200"
                                                            style={{
                                                                borderColor: active ? sec.color : '#F3F4F6',
                                                                background: active ? sec.color + '0d' : '#F9FAFB',
                                                                boxShadow: active ? `0 3px 14px ${sec.color}22` : 'none',
                                                            }}
                                                        >
                                                            <div
                                                                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm"
                                                                style={{
                                                                    background: active
                                                                        ? `linear-gradient(135deg, ${sec.color}, ${sec.color}bb)`
                                                                        : sec.color + '1a',
                                                                }}
                                                            >
                                                                <Icon size={20} color={active ? '#fff' : sec.color} />
                                                            </div>
                                                            <div className="min-w-0 flex-1">
                                                                <p className="text-[12px] font-bold leading-tight"
                                                                   style={{ color: active ? sec.color : '#374151' }}>
                                                                    {sec.label}
                                                                </p>
                                                                <p className="text-[10px] mt-0.5"
                                                                   style={{ color: active ? sec.color + 'aa' : '#9CA3AF' }}>
                                                                    {sec.links.length} bağlantı
                                                                </p>
                                                            </div>
                                                            <ChevronRight
                                                                size={14}
                                                                className="flex-shrink-0 transition-transform duration-200"
                                                                style={{
                                                                    color: active ? sec.color : '#D1D5DB',
                                                                    transform: active ? 'rotate(90deg)' : 'rotate(0deg)',
                                                                }}
                                                            />
                                                        </button>
                                                    );
                                                })}
                                            </div>

                                            {/* Bu satırın alt menüsü */}
                                            <AnimatePresence mode="wait">
                                                {activeSecInRow && (
                                                    <motion.div
                                                        key={activeIdx}
                                                        initial={{ opacity: 0, height: 0 }}
                                                        animate={{ opacity: 1, height: 'auto' }}
                                                        exit={{ opacity: 0, height: 0 }}
                                                        transition={{ duration: 0.16, ease: 'easeOut' }}
                                                        style={{ overflow: 'hidden' }}
                                                    >
                                                        <div
                                                            className="mt-2 rounded-2xl overflow-hidden"
                                                            style={{
                                                                border: `2px solid ${activeSecInRow.color}30`,
                                                                background: `${activeSecInRow.color}07`,
                                                            }}
                                                        >
                                                            {/* Başlık */}
                                                            <div
                                                                className="flex items-center gap-2.5 px-4 py-3"
                                                                style={{
                                                                    background: `${activeSecInRow.color}14`,
                                                                    borderBottom: `1.5px solid ${activeSecInRow.color}20`,
                                                                }}
                                                            >
                                                                {React.createElement(activeSecInRow.icon, { size: 15, color: activeSecInRow.color })}
                                                                <span
                                                                    className="text-[11.5px] font-black uppercase tracking-wider"
                                                                    style={{ color: activeSecInRow.color }}
                                                                >
                                                                    {activeSecInRow.label}
                                                                </span>
                                                            </div>

                                                            {/* Bağlantılar */}
                                                            {activeSecInRow.links.map((link) => (
                                                                link.external ? (
                                                                    <a
                                                                        key={link.title}
                                                                        href={link.href}
                                                                        target="_blank"
                                                                        rel="noopener noreferrer"
                                                                        onClick={close}
                                                                        className="flex items-center gap-3 px-4 py-3 border-b border-white/60 last:border-0 hover:bg-white/70 transition-colors group"
                                                                    >
                                                                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                                                             style={{ backgroundColor: activeSecInRow.color }} />
                                                                        <span className="text-[13.5px] font-medium text-gray-700 group-hover:text-gray-950 flex-1 leading-snug">
                                                                            {link.title}
                                                                        </span>
                                                                        <ExternalLink size={11} className="text-gray-300 flex-shrink-0 group-hover:text-gray-500 transition-colors" />
                                                                    </a>
                                                                ) : (
                                                                    <Link
                                                                        key={link.title}
                                                                        to={link.href}
                                                                        onClick={close}
                                                                        className="flex items-center gap-3 px-4 py-3 border-b border-white/60 last:border-0 hover:bg-white/70 transition-colors group"
                                                                    >
                                                                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                                                                             style={{ backgroundColor: activeSecInRow.color }} />
                                                                        <span className="text-[13.5px] font-medium text-gray-700 group-hover:text-gray-950 flex-1 leading-snug">
                                                                            {link.title}
                                                                        </span>
                                                                        <ArrowRight size={11} className="text-gray-300 flex-shrink-0 group-hover:translate-x-0.5 transition-all" style={{ color: 'inherit' }} />
                                                                    </Link>
                                                                )
                                                            ))}
                                                        </div>
                                                    </motion.div>
                                                )}
                                            </AnimatePresence>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* ── Tekil bağlantılar ── */}
                            <div className="px-5 py-3 flex flex-wrap gap-2 border-t border-gray-50 mt-2">
                                {singleLinks.map((link, idx) => (
                                    <Link key={idx} to={link.href} onClick={close}
                                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-[11.5px] font-semibold text-gray-400 hover:text-[#7B0D1E] hover:bg-red-50 border border-gray-100 hover:border-red-100 transition-all">
                                        <ArrowRight size={10} />
                                        {link.title}
                                    </Link>
                                ))}
                            </div>

                            {/* ── Bölümlerimiz ── */}
                            <div className="px-5 py-4 border-t border-gray-100">
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="text-[11px] font-black uppercase tracking-widest text-gray-500">Bölümlerimiz</span>
                                    <div className="flex-1 h-px bg-gray-200" />
                                </div>
                                <div className="grid grid-cols-5 gap-2">
                                    {deptCards.map(dept => (
                                        <a key={dept.title} href={dept.href} target="_blank" rel="noopener noreferrer" onClick={close}
                                           className="flex flex-col items-center gap-1.5 group">
                                            <div className="w-11 h-11 rounded-2xl flex items-center justify-center text-white text-[10px] font-black shadow-sm group-hover:shadow-md transition-shadow"
                                                 style={{ backgroundColor: dept.color }}>
                                                {dept.short}
                                            </div>
                                            <span className="text-[9px] text-gray-400 text-center leading-tight">{dept.title}</span>
                                        </a>
                                    ))}
                                </div>
                            </div>

                            {/* ── Son Duyuru ── */}
                            {latestAnnouncement && (
                                <div className="px-5 py-4 border-t border-gray-100">
                                    <div className="flex items-center gap-3 mb-3">
                                        <span className="text-[11px] font-black uppercase tracking-widest text-gray-500">Son Duyuru</span>
                                        <div className="flex-1 h-px bg-gray-200" />
                                    </div>
                                    {latestAnnouncement.link?.startsWith('http') ? (
                                        <a href={latestAnnouncement.link} target="_blank" rel="noopener noreferrer" onClick={close}
                                           className="block p-3.5 rounded-xl bg-red-50 border border-red-100 hover:border-[#7B0D1E]/30 hover:shadow-sm transition-all group">
                                            <span className="block w-1.5 h-1.5 rounded-full bg-red-500 mb-2 animate-pulse" />
                                            <p className="text-[12px] text-gray-700 font-semibold leading-snug group-hover:text-[#7B0D1E] line-clamp-2">{latestAnnouncement.title}</p>
                                            <p className="text-[10px] text-gray-400 mt-1.5">{new Date(latestAnnouncement.date).toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long' })}</p>
                                        </a>
                                    ) : (
                                        <Link to={localizePath('/duyurular')} onClick={close}
                                              className="block p-3.5 rounded-xl bg-red-50 border border-red-100 hover:border-[#7B0D1E]/30 hover:shadow-sm transition-all group">
                                            <span className="block w-1.5 h-1.5 rounded-full bg-red-500 mb-2 animate-pulse" />
                                            <p className="text-[12px] text-gray-700 font-semibold leading-snug group-hover:text-[#7B0D1E] line-clamp-2">{latestAnnouncement.title}</p>
                                            <p className="text-[10px] text-gray-400 mt-1.5">{new Date(latestAnnouncement.date).toLocaleDateString(dateLocale(), { day: 'numeric', month: 'long' })}</p>
                                        </Link>
                                    )}
                                    <Link to={localizePath('/duyurular')} onClick={close}
                                          className="flex items-center gap-1 mt-2 text-[11px] text-[#7B0D1E] hover:text-[#9B1A2E] font-bold transition-colors">
                                        Tüm duyurular <ArrowRight size={10} />
                                    </Link>
                                </div>
                            )}

                            {/* ── İstatistikler ── */}
                            <div className="px-5 py-4 border-t border-gray-100">
                                <div className="flex items-center gap-3 mb-3">
                                    <span className="text-[11px] font-black uppercase tracking-widest text-gray-500">Fen Fakültesi</span>
                                    <div className="flex-1 h-px bg-gray-200" />
                                </div>
                                <div className="rounded-xl border border-gray-100 divide-y divide-gray-50 overflow-hidden">
                                    {[
                                        { icon: Layers,        label: 'Bölüm',          value: '5' },
                                        { icon: GraduationCap, label: 'Lisans Programı', value: '5' },
                                        ...(!yayinHata ? [{ icon: BookOpen, label: 'Yayın (2026)',
                                          value: yayinSayisi !== null
                                            ? yayinSayisi.toLocaleString(dateLocale())
                                            : <span className="text-gray-300 text-[11px]">yükleniyor…</span> }] : []),
                                    ].map((stat, i) => {
                                        const Icon = stat.icon;
                                        return (
                                            <div key={i} className="flex items-center gap-3 px-4 py-2.5 bg-white">
                                                <Icon size={12} className="text-gray-300 flex-shrink-0" />
                                                <span className="text-[11.5px] text-gray-500 flex-1">{stat.label}</span>
                                                <span className="text-[13px] font-black text-gray-800">{stat.value}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* bottom padding */}
                            <div className="h-6" />
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

/* ─── ANA NAVBAR ─── */
const Navbar = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isPromotionsOpen, setIsPromotionsOpen] = useState(false);
    const [isStudentMenuOpen, setIsStudentMenuOpen] = useState(false);
    const [isResearchMenuOpen, setIsResearchMenuOpen] = useState(false);
    const [isProgramsMenuOpen, setIsProgramsMenuOpen] = useState(false);
    const [isProgramsOnlyMenuOpen, setIsProgramsOnlyMenuOpen] = useState(false);
    const [showNavSearch, setShowNavSearch] = useState(false);
    const [showSocialMedia, setShowSocialMedia] = useState(false);
    const socialRef = useRef(null);
    const { t, i18n } = useTranslation();

    // Ctrl+K / Cmd+K ile aramayı aç
    useEffect(() => {
        const onKey = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
                e.preventDefault();
                setShowNavSearch(true);
                setShowSocialMedia(false);
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, []);

    // Sosyal medya menüsü dışına tıklanınca kapat
    useEffect(() => {
        if (!showSocialMedia) return;
        const onDown = (e) => { if (socialRef.current && !socialRef.current.contains(e.target)) setShowSocialMedia(false); };
        const onKey = (e) => { if (e.key === 'Escape') setShowSocialMedia(false); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
    }, [showSocialMedia]);
    const navigate = useNavigate();
    const location = useLocation();
    const navLinks = getNavLinks(t, i18n.language);
    const departmentPromotions = getDepartmentPromotions(t, i18n.language);

    const studentMenu  = navLinks.find(link => link.title === (i18n.language === 'en' ? 'Students'      : 'Öğrenciler'));
    const researchMenu = navLinks.find(link => link.title === (i18n.language === 'en' ? 'Research'      : 'Araştırma'));
    const programsMenu = navLinks.find(link => link.title === (i18n.language === 'en' ? 'For Applicants': 'Aday Öğrenciler'));

    const parseGroups = (sublinks) => {
        if (!sublinks) return [];
        const groups = [];
        sublinks.forEach(item => {
            if (item.isGroup) { groups.push({ ...item, items: [] }); }
            else if (groups.length > 0) { groups[groups.length - 1].items.push(item); }
        });
        return groups;
    };
    const programsGroups = parseGroups(programsMenu?.sublinks);
    const studentGroups  = parseGroups(studentMenu?.sublinks);
    const researchGroups = parseGroups(researchMenu?.sublinks);
    const programsOnlyGroup = programsGroups.find(g => g.title === (i18n.language === 'en' ? 'Programs' : 'Programlar'));

    const closeAllMenus = () => {
        setIsPromotionsOpen(false);
        setIsStudentMenuOpen(false);
        setIsResearchMenuOpen(false);
        setIsProgramsMenuOpen(false);
        setIsProgramsOnlyMenuOpen(false);
    };

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.header-dropdown-menu') && !e.target.closest('.department-promotions-menu')) {
                closeAllMenus();
            }
        };
        // Klavye kullanıcıları için: Esc açık menüleri kapatır
        const handleEscape = (e) => { if (e.key === 'Escape') closeAllMenus(); };
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('click', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    // Aynı sayfanın diğer dildeki adresine geçer (eşleşmeler: config/enSlugs.js)
    const changeLanguage = (lang) => {
        const path = location.pathname + location.search;
        navigate(lang === 'en' ? toEnPath(path) : toTrPath(path));
    };

    const currentLang = i18n.language;

    return (
        <>
            <nav className="main-navbar-transparent" aria-label={currentLang === 'en' ? 'Main navigation' : 'Ana menü'}>
                <div className="navbar-container-transparent">
                    {/* Sol: Logo */}
                    <div className="navbar-left">
                        <Link to={currentLang === 'en' ? '/en' : '/'} className="navbar-logo-rebranded">
                            <img src="/fenfaklogo-beyaz.png" alt={currentLang === 'en' ? 'Hacettepe University Faculty of Science logo' : 'Hacettepe Üniversitesi Fen Fakültesi Logosu'} className="main-logo"/>
                            <img src="https://iysportal.hacettepe.edu.tr/templates/template3/images/yokak5.svg" alt={currentLang === 'en' ? 'YÖKAK (Higher Education Quality Council) logo' : 'YÖKAK Logosu'} className="yokak-logo"/>
                        </Link>
                        <div className="navbar-menus">
                            {/* Öğrenciler — 2 sütunlu dropdown */}
                            {studentGroups.length > 0 && (
                                <div className="header-dropdown-menu">
                                    <button className="header-dropdown-toggle student-toggle" aria-label={i18n.language === 'en' ? 'Students' : 'Öğrenciler'} aria-haspopup="true" aria-expanded={isStudentMenuOpen} onClick={(e) => {
                                        e.stopPropagation();
                                        setIsStudentMenuOpen(!isStudentMenuOpen);
                                        setIsResearchMenuOpen(false); setIsPromotionsOpen(false); setIsProgramsMenuOpen(false);
                                    }}>
                                        <GraduationCap size={16} />
                                        <span className="menu-text">{i18n.language === 'en' ? 'Students' : 'Öğrenciler'}</span>
                                        <ChevronDown size={16} className={`transition-transform ${isStudentMenuOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    <AnimatePresence>
                                        {isStudentMenuOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                                                transition={{ duration: 0.18, ease: [0.25, 0.46, 0.45, 0.94] }}
                                                style={{
                                                    position: 'absolute', top: 'calc(100% + 12px)', left: 0,
                                                    width: 460, background: 'rgba(10,15,28,0.97)',
                                                    backdropFilter: 'blur(16px)',
                                                    border: '1px solid rgba(255,255,255,0.09)', borderRadius: 16,
                                                    padding: 6, zIndex: 1000,
                                                    boxShadow: '0 20px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)',
                                                }}
                                            >
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
                                                    {studentGroups.map((group, gIdx) => {
                                                        const GIcon = group.groupIcon ? ICON_MAP[group.groupIcon] : null;
                                                        return (
                                                            <div key={gIdx} style={{ borderRadius: 11, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden', background: 'rgba(255,255,255,0.03)' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 12px 8px', background: 'rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                                                    {GIcon && <GIcon size={11} style={{ color: 'rgba(255,255,255,0.32)', flexShrink: 0 }} />}
                                                                    <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.32)' }}>{group.title}</span>
                                                                </div>
                                                                <div style={{ padding: 4 }}>
                                                                    {group.items.map((item, iIdx) => {
                                                                        const row = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, textDecoration: 'none', transition: 'background 0.12s' };
                                                                        const inner = (<>
                                                                            <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(123,13,30,0.7)', flexShrink: 0 }} />
                                                                            <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.65)', fontWeight: 500, flex: 1 }}>{item.title}</span>
                                                                            {item.external && <ExternalLink size={9} style={{ color: 'rgba(255,255,255,0.18)', flexShrink: 0 }} />}
                                                                        </>);
                                                                        const hov = { onMouseEnter: e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }, onMouseLeave: e => { e.currentTarget.style.background = 'transparent'; } };
                                                                        return item.external
                                                                            ? <a key={iIdx} href={item.href} target="_blank" rel="noopener noreferrer" style={row} {...hov} onClick={() => { setIsStudentMenuOpen(false); trackExternalClick(`Öğrenci - ${item.title}`, item.href); }}>{inner}</a>
                                                                            : <Link key={iIdx} to={item.href} style={row} {...hov} onClick={() => { setIsStudentMenuOpen(false); trackNavigation(`Öğrenci - ${item.title}`, item.href); }}>{inner}</Link>;
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}

                            {/* Programlar — dropdown */}
                            {programsOnlyGroup && (
                                <div className="header-dropdown-menu">
                                    <button className="header-dropdown-toggle programs-toggle" aria-label={i18n.language === 'en' ? 'Programs' : 'Programlar'} aria-haspopup="true" aria-expanded={isProgramsOnlyMenuOpen} onClick={(e) => {
                                        e.stopPropagation();
                                        setIsProgramsOnlyMenuOpen(!isProgramsOnlyMenuOpen);
                                        setIsStudentMenuOpen(false); setIsResearchMenuOpen(false); setIsPromotionsOpen(false); setIsProgramsMenuOpen(false);
                                    }}>
                                        <GraduationCap size={16} />
                                        <span className="menu-text">{i18n.language === 'en' ? 'Programs' : 'Programlar'}</span>
                                        <ChevronDown size={16} className={`transition-transform ${isProgramsOnlyMenuOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    <AnimatePresence>
                                        {isProgramsOnlyMenuOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                                                transition={{ duration: 0.18, ease: [0.25, 0.46, 0.45, 0.94] }}
                                                style={{
                                                    position: 'absolute', top: 'calc(100% + 12px)', left: 0,
                                                    width: 240, background: 'rgba(10,15,28,0.97)',
                                                    backdropFilter: 'blur(16px)',
                                                    border: '1px solid rgba(255,255,255,0.09)', borderRadius: 16,
                                                    padding: 6, zIndex: 1000,
                                                    boxShadow: '0 20px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)',
                                                }}
                                            >
                                                <div style={{ borderRadius: 11, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden', background: 'rgba(255,255,255,0.03)' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 12px 8px', background: 'rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                                        <GraduationCap size={11} style={{ color: 'rgba(255,255,255,0.32)', flexShrink: 0 }} />
                                                        <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.32)' }}>{programsOnlyGroup.title}</span>
                                                    </div>
                                                    <div style={{ padding: 4 }}>
                                                        {programsOnlyGroup.items.map((item, iIdx) => {
                                                            const row = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, textDecoration: 'none', transition: 'background 0.12s' };
                                                            const inner = (<>
                                                                <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(123,13,30,0.7)', flexShrink: 0 }} />
                                                                <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.65)', fontWeight: 500, flex: 1 }}>{item.title}</span>
                                                            </>);
                                                            const hov = { onMouseEnter: e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }, onMouseLeave: e => { e.currentTarget.style.background = 'transparent'; } };
                                                            return <Link key={iIdx} to={item.href} style={row} {...hov} onClick={() => { setIsProgramsOnlyMenuOpen(false); trackNavigation(`Programlar - ${item.title}`, item.href); }}>{inner}</Link>;
                                                        })}
                                                    </div>
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}

                            {/* Araştırma — 2 sütunlu dropdown */}
                            {researchGroups.length > 0 && (
                                <div className="header-dropdown-menu">
                                    <button className="header-dropdown-toggle research-toggle" aria-label={i18n.language === 'en' ? 'Research' : 'Araştırma'} aria-haspopup="true" aria-expanded={isResearchMenuOpen} onClick={(e) => {
                                        e.stopPropagation();
                                        setIsResearchMenuOpen(!isResearchMenuOpen);
                                        setIsStudentMenuOpen(false); setIsPromotionsOpen(false); setIsProgramsMenuOpen(false);
                                    }}>
                                        <Microscope size={16} />
                                        <span className="menu-text">{i18n.language === 'en' ? 'Research' : 'Araştırma'}</span>
                                        <ChevronDown size={16} className={`transition-transform ${isResearchMenuOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    <AnimatePresence>
                                        {isResearchMenuOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                                                transition={{ duration: 0.18, ease: [0.25, 0.46, 0.45, 0.94] }}
                                                style={{
                                                    position: 'absolute', top: 'calc(100% + 12px)', left: 0,
                                                    width: 420, background: 'rgba(10,15,28,0.97)',
                                                    backdropFilter: 'blur(16px)',
                                                    border: '1px solid rgba(255,255,255,0.09)', borderRadius: 16,
                                                    padding: 6, zIndex: 1000,
                                                    boxShadow: '0 20px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)',
                                                }}
                                            >
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 5 }}>
                                                    {researchGroups.map((group, gIdx) => {
                                                        const GIcon = group.groupIcon ? ICON_MAP[group.groupIcon] : null;
                                                        return (
                                                            <div key={gIdx} style={{ borderRadius: 11, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden', background: 'rgba(255,255,255,0.03)' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 12px 8px', background: 'rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                                                    {GIcon && <GIcon size={11} style={{ color: 'rgba(255,255,255,0.32)', flexShrink: 0 }} />}
                                                                    <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.32)' }}>{group.title}</span>
                                                                </div>
                                                                <div style={{ padding: 4 }}>
                                                                    {group.items.map((item, iIdx) => {
                                                                        const row = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, textDecoration: 'none', transition: 'background 0.12s' };
                                                                        const inner = (<>
                                                                            <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(123,13,30,0.7)', flexShrink: 0 }} />
                                                                            <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.65)', fontWeight: 500, flex: 1 }}>{item.title}</span>
                                                                            {item.external && <ExternalLink size={9} style={{ color: 'rgba(255,255,255,0.18)', flexShrink: 0 }} />}
                                                                        </>);
                                                                        const hov = { onMouseEnter: e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }, onMouseLeave: e => { e.currentTarget.style.background = 'transparent'; } };
                                                                        return item.external
                                                                            ? <a key={iIdx} href={item.href} target="_blank" rel="noopener noreferrer" style={row} {...hov} onClick={() => { setIsResearchMenuOpen(false); trackExternalClick(`Araştırma - ${item.title}`, item.href); }}>{inner}</a>
                                                                            : <Link key={iIdx} to={item.href} style={row} {...hov} onClick={() => { setIsResearchMenuOpen(false); trackNavigation(`Araştırma - ${item.title}`, item.href); }}>{inner}</Link>;
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}

                            {/* Adaylar İçin — 2 sütunlu dropdown */}
                            {programsGroups.length > 0 && (
                                <div className="header-dropdown-menu">
                                    <button className="header-dropdown-toggle programs-toggle" aria-label={i18n.language === 'en' ? 'For Applicants' : 'Aday Öğrenciler'} aria-haspopup="true" aria-expanded={isProgramsMenuOpen} onClick={(e) => {
                                        e.stopPropagation();
                                        setIsProgramsMenuOpen(!isProgramsMenuOpen);
                                        setIsStudentMenuOpen(false); setIsResearchMenuOpen(false); setIsPromotionsOpen(false);
                                    }}>
                                        <Layers size={16} />
                                        <span className="menu-text">{i18n.language === 'en' ? 'For Applicants' : 'Aday Öğrenciler'}</span>
                                        <ChevronDown size={16} className={`transition-transform ${isProgramsMenuOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    <AnimatePresence>
                                        {isProgramsMenuOpen && (
                                            <motion.div
                                                initial={{ opacity: 0, y: -8, scale: 0.98 }}
                                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                                exit={{ opacity: 0, y: -8, scale: 0.98 }}
                                                transition={{ duration: 0.18, ease: [0.25, 0.46, 0.45, 0.94] }}
                                                style={{
                                                    position: 'absolute',
                                                    top: 'calc(100% + 12px)',
                                                    left: 0,
                                                    width: 660,
                                                    background: 'rgba(10,15,28,0.97)',
                                                    backdropFilter: 'blur(16px)',
                                                    border: '1px solid rgba(255,255,255,0.09)',
                                                    borderRadius: 16,
                                                    padding: 6,
                                                    zIndex: 1000,
                                                    boxShadow: '0 20px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)',
                                                }}
                                            >
                                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 5 }}>
                                                    {programsGroups.map((group, gIdx) => {
                                                        const GIcon = group.groupIcon ? ICON_MAP[group.groupIcon] : null;
                                                        return (
                                                            <div key={gIdx} style={{ borderRadius: 11, border: '1px solid rgba(255,255,255,0.07)', overflow: 'hidden', background: 'rgba(255,255,255,0.03)' }}>
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 12px 8px', background: 'rgba(255,255,255,0.05)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                                                    {GIcon && <GIcon size={11} style={{ color: 'rgba(255,255,255,0.32)', flexShrink: 0 }} />}
                                                                    <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.32)' }}>{group.title}</span>
                                                                </div>
                                                                <div style={{ padding: 4 }}>
                                                                    {group.items.map((item, iIdx) => {
                                                                        const row = { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, textDecoration: 'none', transition: 'background 0.12s' };
                                                                        const inner = (<>
                                                                            <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'rgba(123,13,30,0.7)', flexShrink: 0 }} />
                                                                            <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.65)', fontWeight: 500, flex: 1, textDecoration: item.isNew ? 'underline' : 'none', textDecorationColor: 'rgba(220,38,38,0.7)', textUnderlineOffset: 3 }}>{item.title}</span>
                                                                            {item.isNew && (
                                                                                <span style={{ fontSize: 8, fontWeight: 900, letterSpacing: '0.06em', color: '#fff', background: '#DC2626', borderRadius: 4, padding: '1.5px 5px', flexShrink: 0 }}>
                                                                                    {i18n.language === 'en' ? 'NEW' : 'YENİ'}
                                                                                </span>
                                                                            )}
                                                                            {item.external && <ExternalLink size={9} style={{ color: 'rgba(255,255,255,0.18)', flexShrink: 0 }} />}
                                                                        </>);
                                                                        const hov = { onMouseEnter: e => { e.currentTarget.style.background = 'rgba(255,255,255,0.06)'; }, onMouseLeave: e => { e.currentTarget.style.background = 'transparent'; } };
                                                                        return item.external
                                                                            ? <a key={iIdx} href={item.href} target="_blank" rel="noopener noreferrer" style={row} {...hov} onClick={() => { setIsProgramsMenuOpen(false); trackExternalClick(`Adaylar - ${item.title}`, item.href); }}>{inner}</a>
                                                                            : <Link key={iIdx} to={item.href} style={row} {...hov} onClick={() => { setIsProgramsMenuOpen(false); trackNavigation(`Adaylar - ${item.title}`, item.href); }}>{inner}</Link>;
                                                                    })}
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            )}

                            <Link
                                to={i18n.language === 'en' ? '/en/student/international-students' : '/ogrenci/uluslararasi-ogrenciler'}
                                className="announcements-btn"
                                title={t('nav.internationalStudents')}
                                aria-label={t('nav.internationalStudents')}
                                onClick={() => trackButtonClick('International Students', 'header')}
                            >
                                <Globe size={16} />
                                <span>{t('nav.internationalStudents')}</span>
                            </Link>

                            <Link to={localizePath('/duyurular')} className="announcements-btn"
                                  title={t('navbar.announcements')} aria-label={t('navbar.announcements')}
                                  onClick={() => trackButtonClick('Duyurular', 'header')}>
                                <Bell size={16} />
                                <span>{t('navbar.announcements')}</span>
                            </Link>
                        </div>
                    </div>

                    {/* Sağ: Arama + Dil + Menü */}
                    <div className="navbar-right" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {/* Arama — mobilde gizli */}
                        <div className="hidden sm:contents">
                        {/* Arama — sayfanın ortasında açılan pencere (Ctrl+K) */}
                        <SearchModal open={showNavSearch} onClose={() => setShowNavSearch(false)} />

                        <button
                            onClick={() => { setShowNavSearch(true); setShowSocialMedia(false); }}
                            className="navbar-search-btn"
                            title={currentLang === 'en' ? 'Search the site (Ctrl+K)' : 'Sitede ara (Ctrl+K)'}
                            aria-label={currentLang === 'en' ? 'Search the site' : 'Sitede ara'}
                            aria-haspopup="dialog"
                        >
                            <Search size={18} />
                        </button>
                        </div>

                        {/* Sosyal Medya Toggle — mobilde gizli */}
                        <div className="hidden sm:contents">
                        <div ref={socialRef} style={{ position: 'relative', flexShrink: 0 }}>
                            <button
                                onClick={() => { setShowSocialMedia(s => !s); setShowNavSearch(false); }}
                                className="navbar-search-btn"
                                title={currentLang === 'en' ? 'Social media' : 'Sosyal Medya'}
                                aria-label={currentLang === 'en' ? 'Social media' : 'Sosyal medya'}
                                aria-haspopup="true"
                                aria-expanded={showSocialMedia}
                            >
                                <Share2 size={17} />
                            </button>
                            {/* Butonun altında açılan menü — diğer öğelerin üstüne binmez */}
                            <AnimatePresence>
                                {showSocialMedia && (
                                    <motion.div
                                        key="social-dropdown"
                                        initial={{ opacity: 0, y: -6 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: -6 }}
                                        transition={{ duration: 0.18, ease: [0.25, 0.46, 0.45, 0.94] }}
                                        style={{
                                            position: 'absolute', top: 'calc(100% + 10px)', right: 0, zIndex: 1000,
                                            minWidth: 190, padding: 6, borderRadius: 12,
                                            background: '#fff', border: '1px solid #e5e7eb',
                                            boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
                                            display: 'flex', flexDirection: 'column', gap: 2,
                                        }}
                                    >
                                        {HEADER_SOCIAL.map(({ href, Icon, title, color }) => (
                                            <a key={title} href={href} target="_blank" rel="noopener noreferrer"
                                               aria-label={`${title} (${currentLang === 'en' ? 'opens in new tab' : 'yeni sekmede açılır'})`}
                                               onClick={() => setShowSocialMedia(false)}
                                               style={{
                                                   display: 'flex', alignItems: 'center', gap: 10,
                                                   padding: '8px 10px', borderRadius: 8,
                                                   color: '#1f2937', fontSize: 14, fontWeight: 500,
                                                   textDecoration: 'none', transition: 'background 0.15s',
                                               }}
                                               onMouseEnter={e => { e.currentTarget.style.background = '#f3f4f6'; }}
                                               onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                            >
                                                <span style={{
                                                    width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    background: color, color: '#fff',
                                                }}>
                                                    <Icon size={14} />
                                                </span>
                                                {title}
                                            </a>
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                        </div>

                        <button onClick={() => changeLanguage(currentLang === 'tr' ? 'en' : 'tr')}
                                className="language-switcher-btn" title={currentLang === 'tr' ? 'Switch to English' : "Türkçe'ye Geç"}
                                aria-label={currentLang === 'tr' ? 'Switch to English' : "Türkçe'ye geç"}>
                            <Globe size={16} aria-hidden="true" />
                            <span aria-hidden="true">{currentLang === 'tr' ? 'EN' : 'TR'}</span>
                        </button>
                        <button className="menu-toggle-btn-transparent" onClick={() => setIsOpen(true)}
                                aria-label={currentLang === 'en' ? 'Open menu' : 'Menüyü aç'} aria-expanded={isOpen}>
                            <MenuIcon />
                        </button>
                    </div>
                </div>

            </nav>

            <MegaMenu isOpen={isOpen} setIsOpen={setIsOpen} changeLanguage={changeLanguage} currentLang={currentLang} />
        </>
    );
};

export default Navbar;
